/* eslint-disable no-console */

import * as otel from '@opentelemetry/api';
import type { AppEnv } from '../env.ts';
import { BaseLogger } from './base.ts';
import { JSONReplacer } from './helper.ts';
import type { LogLevel, Data } from './index.ts';
import { captureException } from './sentry.ts';

type StructuredPayload = Record<string, unknown>;

// see: https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#LogSeverity
// see: https://cloud.google.com/logging/docs/structured-logging?hl=ja#special-payload-fields
const logSeverity = {
  DEFAULT: 'DEFAULT',
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  NOTICE: 'NOTICE',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  CRITICAL: 'CRITICAL',
  ALERT: 'ALERT',
  EMERGENCY: 'EMERGENCY',
} as const;

const logSeverityLogLevelMap = {
  log: logSeverity.DEBUG,
  info: logSeverity.INFO,
  error: logSeverity.ERROR,
  warn: logSeverity.WARNING,
} as const satisfies Record<LogLevel, keyof typeof logSeverity>;

export class ServerLogger extends BaseLogger {
  appEnv: AppEnv;
  projectId: string | undefined;

  constructor(appEnv: AppEnv, projectId?: string) {
    super();

    this.appEnv = appEnv;
    this.projectId = projectId;
  }

  protected exec(level: LogLevel, message: unknown, data?: Data) {
    // NOTE: 開発時のコンソール出力は構造化しない
    if (this.appEnv === 'local') {
      if (data) {
        console[level](message, data);
      } else {
        console[level](message);
      }

      return;
    }

    const severity = logSeverityLogLevelMap[level];

    const payload: Record<string, unknown> = {
      severity,
      message,
      data,
      ...this.getTrace(),
    };

    if (message instanceof Error) {
      // messageフィールドはLog Explorer上でサマリとして扱われるため特別対応
      // String(error) -> `${error.name}: ${error.message}`
      payload.message = String(message);
      payload.error = message;
    }

    const value = JSON.stringify(payload, JSONReplacer);

    console[level](value);

    if (level === 'error' || level === 'warn') {
      captureException(level, message, data);
    }
  }

  private getTrace(): StructuredPayload | undefined {
    const span = otel.trace.getSpan(otel.context.active());
    if (!span) return;

    const { traceId, spanId, traceFlags } = span.spanContext();

    return {
      'logging.googleapis.com/trace':
        this.projectId && traceId ? `projects/${this.projectId}/traces/${traceId}` : undefined,
      'logging.googleapis.com/spanId': spanId,
      'logging.googleapis.com/trace_sampled': traceFlags ? traceFlags === 1 : undefined,
    };
  }
}
